import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  AspectRatio,
  Chip,
  Divider,
} from '@mui/joy';
import { formatTotalPrice } from '../utils/helpers.js';
import { TrendAnalysis } from '../../libs/poshwatch_ts_client/api';
import { StatsCard } from '../pages/search/components/StatsCard.tsx';

// Shared style card component to prevent code duplication
export const StyleCard = React.memo<{
  style: TrendAnalysis;
  index: number;
}>(({ style, index }) => {
  const getFirstSoldImage = useCallback((soldPosts: any[] | undefined) => {
    if (!soldPosts || soldPosts.length === 0) return null;
    return soldPosts[0].picture_url;
  }, []);

  const firstSoldImage = useMemo(() => getFirstSoldImage(style.soldPosts), [style.soldPosts, getFirstSoldImage]);

  // Memoize the filter chips to prevent recreation
  const filterChips = useMemo(() => {
    if (!style.filters) return null;

    return Object.entries(style.filters)
      .filter(([key, value]) => key === 'styles' && Array.isArray(value) && value.length > 0)
      .map(([key, value]) =>
        (value as string[]).map((val, i) => (
          <Chip key={key + val + i} size="lg" variant="soft" color="primary">
            {val}
          </Chip>
        ))
      );
  }, [style.filters]);

  // Filter out price stats and memoize the remaining stats cards
  const statsCards = useMemo(() => {
    const nonPriceStats = style.stats.filter((stat: any) => stat.title !== 'Average Price');
    return nonPriceStats.map((stat: any, i: number) => (
      <Box
        key={i}
        sx={{
          flex: '1 1 220px',
          minWidth: 220,
          border: 'none',
        }}
      >
        <StatsCard {...stat} />
      </Box>
    ));
  }, [style.stats]);

  return (
    <Box
      sx={{
        background: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 8,
        mb: 3,
        px: 2,
        py: { xs: 3, sm: 4 },
        boxShadow: 'none',
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {firstSoldImage && (
          <AspectRatio ratio="1" sx={{ width: 160, flexShrink: 0 }}>
            <Box
              component="img"
              src={firstSoldImage}
              alt={style.query}
              loading="lazy"
              sx={{
                objectFit: 'cover',
                borderRadius: '8px'
              }}
            />
          </AspectRatio>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography level="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
            {style.query}
          </Typography>

          {/* Render filter chips below the prices */}
          {filterChips && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {filterChips}
            </Box>
          )}

          {/* Price information displayed directly under the title */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
            <Box>
              <Typography level="body-sm" color="neutral">
                Avg. sold price: {style.coreMetrics.averageSoldPrice > 0 ? formatTotalPrice(style.coreMetrics.averageSoldPrice) : 'N/A'}
              </Typography>
            </Box>
            <Box
              sx={{
                height: '16px',
                width: '1px',
                bgcolor: 'divider',
              }}
            />
            <Box>
              <Typography level="body-sm" color="neutral">
                Avg. available price: {style.coreMetrics.averageAvailablePrice > 0 ? formatTotalPrice(style.coreMetrics.averageAvailablePrice) : 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {statsCards}
      </Box>
    </Box>
  );
});

StyleCard.displayName = 'StyleCard';
